import React from "react";
import TextField from "@mui/material/TextField/TextField";
import GenericSelect from "../GenericSelect";
import {InputField, InputFieldType} from "./types";
import {FormikProps} from "formik";
import StageSelect from "../StageSelect";
import CountrySelect from "../CountrySelect";
import LanguageSelect from "../LanguageSelect";
import FormDatePicker from "../FormDatePicker";
import _, {get} from "lodash";
import FormTextInputField from "../FormTextField";
import {useTranslation} from "react-i18next";
import FormSelectListRequestField from "../FormSelectListRequestField";
import FormCheckboxField from "../FormCheckboxField";
import getCompleteFieldName from "./helper";
import {Grid} from "@mui/material";
import {LabeledValue} from "../../Layout/Page";
import {EMPTY_UI} from "../../../variables";
import {ListRequest, ListResponseType} from "../../../app/api/types";
import OrganizationStageSelect from "../OrganizationStageSelect";

interface InputFactoryProps<T, V extends ListResponseType, U extends ListRequest> {
    inputField: InputField<T, V, U>;
    form: FormikProps<Partial<T>>;
}

export default function InputFactory<T, V extends ListResponseType, U extends ListRequest>({
    inputField,
    form,
}: InputFactoryProps<T, V, U>): JSX.Element {
    const {t} = useTranslation();
    const {
        translationKey,
        fieldName,
        subFieldName,
        type = InputFieldType.TEXT,
        selectOptions,
        render,
        query,
        request,
        fieldProps,
        valueField,
        optionLabelField,
        ...rest
    } = inputField;
    const name = getCompleteFieldName(fieldName, subFieldName);
    const value = get(form.values, name);

    if (render) {
        return render({inputField: _.omit(inputField, ["render"]), form});
    } else {
        switch (type) {
            case InputFieldType.READ_ONLY:
                return (
                    <Grid item {...rest.gridProps}>
                        <LabeledValue label={t(translationKey)} content={value ? value : EMPTY_UI} {...fieldProps} />
                    </Grid>
                );
            case InputFieldType.STAGE_SELECT:
                return (
                    <StageSelect
                        label={t(translationKey)}
                        fieldName={name}
                        form={form}
                        t={t}
                        isGridItem={true}
                        {...rest}
                        {...fieldProps}
                    />
                );
            case InputFieldType.ORGANIZATION_STAGE_SELECT:
                return (
                    <OrganizationStageSelect
                        label={t(translationKey)}
                        fieldName={name}
                        form={form}
                        t={t}
                        isGridItem={true}
                        {...rest}
                        {...fieldProps}
                    />
                );
            case InputFieldType.COUNTRY:
                return (
                    <CountrySelect
                        label={t(translationKey)}
                        fieldName={name}
                        form={form}
                        t={t}
                        isGridItem={true}
                        {...rest}
                        {...fieldProps}
                    />
                );
            case InputFieldType.LANGUAGE:
                return (
                    <LanguageSelect
                        label={t(translationKey)}
                        fieldName={name}
                        form={form}
                        t={t}
                        isGridItem={true}
                        {...rest}
                        {...fieldProps}
                    />
                );
            case InputFieldType.DATE:
                return (
                    <FormDatePicker
                        label={t(translationKey)}
                        fieldName={name}
                        form={form}
                        t={t}
                        isGridItem={true}
                        {...rest}
                        {...fieldProps}
                    />
                );
            case InputFieldType.SELECT:
                if (!selectOptions) {
                    throw Error('"selectOptions" must be defined');
                }
                return (
                    <GenericSelect
                        fieldName={name}
                        label={t(translationKey)}
                        form={form}
                        t={t}
                        isGridItem={true}
                        selectOptions={selectOptions || []}
                        {...fieldProps}
                    />
                );
            case InputFieldType.SELECT_FROM_REQUEST:
                if (!query) {
                    throw Error('"query" must be defined');
                }
                if (!request) {
                    throw Error('"request" must be defined');
                }
                return (
                    <FormSelectListRequestField<V, Partial<T>, U>
                        fieldName={fieldName}
                        subFieldName={subFieldName}
                        query={query}
                        request={request}
                        label={t(translationKey)}
                        form={form}
                        isGridItem={true}
                        optionLabelField={optionLabelField}
                        {...rest}
                        {...fieldProps}
                    />
                );
            case InputFieldType.HIDDEN:
                return (
                    <TextField
                        label={t(translationKey)}
                        name={name}
                        value={value}
                        type={"hidden"}
                        variant={"standard"}
                        {...rest}
                        {...fieldProps}
                    />
                );
            case InputFieldType.CHECKBOX:
                return (
                    <FormCheckboxField<Partial<T>>
                        fieldName={fieldName}
                        subFieldName={subFieldName}
                        label={t(translationKey)}
                        form={form}
                        isGridItem
                        {...rest}
                        {...fieldProps}
                    />
                );

            case InputFieldType.INTEGER:
                return (
                    <FormTextInputField
                        label={t(translationKey)}
                        fieldName={name}
                        form={form}
                        isGridItem={true}
                        type={type}
                        enforceCorrectness={(x: string) => x.replaceAll(/\D+/g, "")}
                        {...rest}
                        {...fieldProps}
                    />
                );

            case InputFieldType.NUMBER:
            case InputFieldType.TEXT:
            default:
                return (
                    <FormTextInputField
                        label={t(translationKey)}
                        fieldName={name}
                        form={form}
                        isGridItem={true}
                        type={type}
                        {...rest}
                        {...fieldProps}
                    />
                );
        }
    }
}
